import React, { useEffect, useRef, useState } from 'react';
import { Database, Globe, BarChart3, Users, Cpu, AlertCircle, ZoomIn, ZoomOut, RotateCcw } from 'lucide-react';



const getNodeColor = (type, status) => {
  // if (type === 'contract') return '#3B82F6'; // Blue for contract
  
  switch (status) {
    case 'healthy':
      return '#10B981'; // Green
    case 'warning':
      return 'orange'; // Ornage
    case 'critical':
      return '#EF4444'; // Red
    default:
      return '#EF4444';
  }
};

const getNodeIcon = (type) => {
  switch (type) {
    case 'contract':
      return Database;
    case 'service':
      return Cpu;
    case 'dashboard':
      return BarChart3;
    case 'ml_model':
      return Database;
    case 'api':
      return Globe;
    case 'report':
      return Users;
    default:
      return Globe;
  }
};

const priority = {
  healthy: 1,
  warning: 2,
  critical: 3,
};

const getWorstStatus = (status1, status2) => {
  // Normalize to lowercase strings
  const s1 = (status1 || "").toLowerCase();
  const s2 = (status2 || "").toLowerCase();

  const p1 = priority[s1] || 0; // 0 = invalid
  const p2 = priority[s2] || 0;

  // If both are invalid or empty → treat as critical
  if (p1 === 0 && p2 === 0) return "critical";

  // Return the worse (higher priority)
  return p1 >= p2 ? s1 : s2;
};

export const UpstreamNetworkView = ({ 
  consumers, 
  contractName,
  contractStatus
}) => {
  const svgRef = useRef(null);
  const [zoom, setZoom] = useState(1.2);
  const [pan, setPan] = useState({ x: 0, y: 420 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [selectedNode, setSelectedNode] = useState(null);

  // Convert consumer data to nodes and edges with proper hierarchy
  const { nodes, edges } = React.useMemo(() => {
    const nodeMap = new Map();
    const edgeList = [];

    // Add root contract node at the center top
    nodeMap.set('contract', {
      id: 'contract',
      name: contractName,
      type: 'contract',
      status: contractStatus,
      x: 400,
      y: -80,
      level: 0
    });

    // Calculate positions for hierarchical layout
    const levelHeight = 120;
    const nodeSpacing = 180;

    // First, collect all nodes by level
    const nodesByLevel= [[], [], []]; // Support up to 3 levels deep

    const collectNodes = (consumer, level) => {
      if (level < nodesByLevel.length) {
        nodesByLevel[level].push(consumer);
      }
      
      if (consumer.dependencies) {
        consumer.dependencies.forEach(dep => collectNodes(dep, level + 1));
      }
    };

    // Collect all consumers and their dependencies
    consumers.forEach(consumer => collectNodes(consumer, 0));

    // Position nodes level by level
    nodesByLevel.forEach((levelNodes, level) => {
      if (levelNodes.length === 0) return;

      const y = -(80 + (level + 1) * levelHeight);
      const totalWidth = (levelNodes.length - 1) * nodeSpacing;
      const startX = 400 - totalWidth / 2;

      levelNodes.forEach((consumer, index) => {
        const x = startX + index * nodeSpacing;
        const existingStatus = nodeMap.get(consumer.id)?.status;

        nodeMap.set(consumer.id, {
          id: consumer.id,
          name: consumer.name,
          type: consumer.type,
          status: getWorstStatus(existingStatus, consumer.status),
          x,
          y,
          level: level + 1,
          parentId: level === 0 ? 'contract' : undefined
        });
      });
    });

    // Create edges based on the hierarchy
    const createEdges = (consumer, parentId) => {
      edgeList.push({ from: parentId, to: consumer.id });
      
      if (consumer.dependencies) {
        consumer.dependencies.forEach(dep => {
          createEdges(dep, consumer.id);
        });
      }
    };

    // Create edges from contract to first level consumers
    consumers.forEach(consumer => {
      createEdges(consumer, 'contract');
    });

    return { nodes: Array.from(nodeMap.values()), edges: edgeList };
  }, [consumers, contractName]);

  const handleZoomIn = () => setZoom(prev => Math.min(prev * 1.2, 3));
  const handleZoomOut = () => setZoom(prev => Math.max(prev / 1.2, 0.3));
  const handleReset = () => {
    setZoom(1.2);
    setPan({ x: 0, y: 420 });
    setSelectedNode(null);
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      setPan({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 h-[550px] relative overflow-hidden">
      {/* Controls */}
      <div className="absolute top-4 right-4 z-10 flex gap-2">
        <button
          onClick={handleZoomIn}
          className="p-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
          title="Zoom In"
        >
          <ZoomIn className="  h-4 text-gray-600" />
        </button>
        <button
          onClick={handleZoomOut}
          className="p-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
          title="Zoom Out"
        >
          <ZoomOut className="  h-4 text-gray-600" />
        </button>
        <button
          onClick={handleReset}
          className="p-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
          title="Reset View"
        >
          <RotateCcw className="  h-4 text-gray-600" />
        </button>
      </div>

      {/* Network Visualization */}
      <svg
        ref={svgRef}
        className="w-full h-full cursor-grab active:cursor-grabbing"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <defs>
          <marker
            id="arrowhead"
            markerWidth="4"
            markerHeight="3"
            refX="3.5"
            refY="1.5"
            orient="auto"
          >
            <polygon points="0 0, 4 1.5, 0 3" fill="#6B7280" />
          </marker>
          <filter id="glow">
            <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
            <feMerge> 
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
          
          {/* Gradient for connection lines */}
          <linearGradient id="connectionGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.8"/>
            <stop offset="100%" stopColor="#6B7280" stopOpacity="0.6"/>
          </linearGradient>
        </defs>

        <g transform={`translate(${pan.x}, ${pan.y}) scale(${zoom})`}>
          {/* Background grid for better visual reference */}
          <defs>
            <pattern id="grid" width="50" height="50" patternUnits="userSpaceOnUse">
              <path d="M 50 0 L 0 0 0 50" fill="none" stroke="#f3f4f6" strokeWidth="1" opacity="0.3"/>
            </pattern>
          </defs>
          <rect width="800" height="600" fill="url(#grid)" />

          {/* Level indicators */}
          {/* {[1, 2, 3].map(level => (
            <g key={level}>
              <line
                x1="50"
                y1={-(80 + level * 120)}
                x2="750"
                y2={-(80 + level * 120)}
                stroke="#e5e7eb"
                strokeWidth="1"
                strokeDasharray="5,5"
                opacity="0.5"
              />
              <text
                x="60"
                y={-(80 + level * 120 - 10)}
                className="text-xs fill-gray-400"
                style={{ fontSize: '10px' }}
              >
                Level {level}
              </text>
            </g>
          ))} */}

          {/* Edges with improved styling */}
          {edges.map((edge, index) => {
            const fromNode = nodes.find(n => n.id === edge.from);
            const toNode = nodes.find(n => n.id === edge.to);
            if (!fromNode || !toNode) return null;

            const isHighlighted = selectedNode && (selectedNode === edge.from || selectedNode === edge.to);
            const opacity = selectedNode && !isHighlighted ? 0.2 : 0.8;

            return (
              <g key={index}>
                {/* Connection line */}
                <line
                  x1={toNode.x}
                  y1={toNode.y + (toNode.type === 'contract' ? 30 : 25)}
                  x2={fromNode.x}
                  y2={fromNode.y - 25}
                  stroke={isHighlighted ? "#3B82F6" : "#9CA3AF"}
                  strokeWidth={isHighlighted ? "3" : "2"}
                  markerEnd="url(#arrowhead)"
                  opacity={opacity}
                  strokeDasharray={"4 6"} // {isHighlighted ? "0" : "4 6"}  // Dashed lines for normal, solid for highlight
                  className="transition-all duration-200 dotted-line"
                />
                
                {/* Data flow animation for highlighted connections */}
                {isHighlighted && (
                  <circle r="3" fill="#3B82F6" opacity="0.8">
                    <animateMotion
                      dur="2s"
                      repeatCount="indefinite"
                      path={`M ${toNode.x} ${toNode.y + 30 } L ${fromNode.x} ${fromNode.y - 25 }`}
                    />
                  </circle>
                )}
              </g>
            );
          })}

          {/* Nodes */}
          {nodes.map((node) => {
            const Icon = getNodeIcon(node.type);
            const color = getNodeColor(node.type, node.status);
            const isSelected = selectedNode === node.id;
            const isContract = node.type === 'contract';
            const radius = isContract ? 30 : 25;
            const shouldBlink = node.name?.trim() === contractName.trim();
            return (
              <g key={node.id}>
                {/* Node shadow */}
                <circle
                  cx={node.x + 2}
                  cy={node.y + 2}
                  r={radius}
                  fill="rgba(0,0,0,0.1)"
                  className="pointer-events-none"
                />

                {isContract ?
                  <circle
                    cx={node.x}
                    cy={node.y}
                    r={radius}
                    fill={color}
                    stroke={isSelected ? '#3B82F6' : '#ffffff'}
                    strokeWidth={isSelected ? 4 : 3}
                    className="cursor-pointer transition-all duration-200 hover:stroke-blue-400 hover:stroke-4"
                    filter={isSelected ? 'url(#glow)' : undefined}
                    onClick={() => setSelectedNode(isSelected ? null : node.id)}
                  /> : 
                    <rect
                    x={node.x - radius}
                    y={node.y - radius}
                    width={radius * 2}
                    height={radius * 2}
                    fill={color}
                    stroke={isSelected ? '#3B82F6' : '#ffffff'}
                    strokeWidth={isSelected ? 4 : 3}
                    className={`cursor-pointer transition-all duration-200 hover:stroke-blue-400 hover:stroke-4 ${shouldBlink ? "node-blink" : "" }`}
                    filter={isSelected ? 'url(#glow)' : undefined}
                    onClick={() => setSelectedNode(isSelected ? null : node.id)}
                  />
                }
                {/* Node icon */}
                <foreignObject
                  x={node.x - 12}
                  y={node.y - 7}
                  width="20"
                  height="20"
                  className="pointer-events-none"
                >
                  <Icon className="  h-4 text-white" />
                </foreignObject>

                {/* Status indicator for error nodes */}
                {node.status === 'error' && (
                  <g>
                    <circle
                      cx={node.x + radius - 8}
                      cy={node.y - radius + 8}
                      r="8"
                      fill="#EF4444"
                      stroke="#ffffff"
                      strokeWidth="2"
                    />
                    <foreignObject
                      x={node.x + radius - 12}
                      y={node.y - radius + 4}
                      width="8"
                      height="8"
                      className="pointer-events-none"
                    >
                      <AlertCircle className="w-2 h-2 text-white" />
                    </foreignObject>
                  </g>
                )}

                {/* Node label */}
                <text
                  x={node.x}
                  y={node.y + radius + 15}
                  textAnchor="middle"
                  className={`font-medium pointer-events-none ${
                    isContract ? 'text-sm fill-gray-800' : 'text-xs fill-gray-700'
                  }`}
                  style={{ fontSize: isContract ? '12px' : '11px' }}
                >
                  {/* {node.name.length > (isContract ? 20 : 15) 
                    ? `${node.name.substring(0, isContract ? 20 : 15)}...` 
                    : node.name} */}
                    {node.name}
                </text>

                {/* Type label */}
                {!isContract && node.name.trim() === contractName.trim() && (<text
                  x={node.x}
                  y={node.y + radius + (isContract ? 28 : 26)}
                  textAnchor="middle"
                  className="text-xs fill-gray-500 pointer-events-none"
                  style={{ fontSize: '9px' }}
                >
                  (Circular dependency)
                </text>)}

                {/* Level indicator for non-contract nodes */}
                {!isContract && (
                  <text
                    x={node.x - radius - 15}
                    y={node.y + 3}
                    textAnchor="middle"
                    className="text-xs fill-gray-400 pointer-events-none font-mono"
                    style={{ fontSize: '8px' }}
                  >
                    L{node.level}
                  </text>
                )}
              </g>
            );
          })}
        </g>
      </svg>

      {/* Legend */}
      {/* <div className="absolute bottom-4 left-4 bg-white border border-gray-300 rounded-lg p-3 shadow-sm">
        <div className="text-xs font-medium text-gray-700 mb-2">Legend</div>
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-xs">
            <div className="  h-4 bg-blue-500 rounded-full border-2 border-white"></div>
            <span className="text-blue-600">Data Contract</span>
          </div>
          <div className="flex gap-3 text-xs">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <span className="text-green-600">healthy</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
              <span className="text-orange-600">warning</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-red-500 rounded-full"></div>
              <span className="text-red-600">critical</span>
            </div>
          </div>
        </div>
      </div> */}

      {/* Selected Node Info */}
      {selectedNode && (
        <div className="absolute top-4 left-4 bg-white border border-gray-300 rounded-lg p-4 shadow-lg max-w-xs">
          {(() => {
            const node = nodes.find(n => n.id === selectedNode);
            if (!node) return null;
            
            const consumer = selectedNode === 'contract' ? null : 
              [...consumers, ...consumers.flatMap(c => c.dependencies || [])].find(c => c.id === selectedNode);

            return (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <div 
                    className="  h-4 rounded-full border-2 border-white"
                    style={{ backgroundColor: getNodeColor(node.type, node.status) }}
                  ></div>
                  <div className="font-medium text-gray-900">{node.name}</div>
                </div>
                <div className="text-xs text-gray-600 space-y-1">
                  <div>Type: <span className="font-medium">{node.type?.replace('_', ' ') || 'product'}</span></div>
                  <div>Level: <span className="font-medium">{node.level}</span></div>
                  {consumer && (
                    <>
                      <div>Status: <span className={`font-medium ${
                        consumer.status === 'healthy' ? 'text-green-600' :
                        consumer.status === 'warning' ? 'text-orange-600' : 'text-red-600'
                      }`}>
                        {consumer.status}
                      </span></div>
                      {/* <div>Last accessed: <span className="font-medium">{consumer.lastAccessed}</span></div> */}
                    </>
                  )}
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* Instructions */}
      <div className="absolute bottom-4 right-4 bg-blue-50 border border-blue-200 rounded-lg p-2 text-xs text-blue-700">
        <div className="font-medium mb-1">Instructions:</div>
        <div>• Click nodes to highlight connections</div>
        <div>• Drag to pan • Use zoom controls</div>
      </div>
    </div>
  );
};